import { Button, Checkbox, Modal } from "@xola/ui-kit";
import clsx from "clsx";
import React from "react";
import { LineItemCode, LineItemSelectorType, LineItemTemplate, LineItemType } from "types";
import { SelectionState, useSelection } from "../../hooks/useSelection";
import { InfiniteList } from "./InfiniteList";
import { LineItemTemplateImage } from "./LineItemTemplateImage";
import { NoTranslate } from "./NoTranslate";
import { SecondaryButton } from "./SecondaryButton";
import { useInfiniteCollection } from "@/hooks/useInfiniteCollection";

interface LineItemSelectorModalProps {
    isOpen: boolean;
    onClose: () => void;
    value?: SelectionState;
    onChange: (value: SelectionState, secondaryValue?: SelectionState) => void;
    title: string;
    type: LineItemSelectorType;
    allowedIds?: ID[];
    /** Renders a second, independently selectable section — used when one rule spans two unit types. */
    secondaryType?: LineItemSelectorType;
    secondaryValue?: SelectionState;
    secondaryAllowedIds?: ID[];
    sectionLabels?: { primary: string; secondary: string };
}

export const LineItemSelectorModal = ({ isOpen, onClose, ...rest }: LineItemSelectorModalProps) => {
    return (
        <Modal isOpen={isOpen} size="huge" onClose={onClose}>
            <ModalContent {...rest} onClose={onClose} />
        </Modal>
    );
};

interface ModalContentProps extends Omit<LineItemSelectorModalProps, "isOpen"> {}

const ModalContent = ({
    onClose,
    value,
    onChange,
    title,
    type,
    allowedIds,
    secondaryType,
    secondaryValue,
    secondaryAllowedIds,
    sectionLabels,
}: ModalContentProps) => {
    const primary = useTemplateSelection(type, value, allowedIds);
    const secondary = useTemplateSelection(secondaryType, secondaryValue, secondaryAllowedIds);

    const handleConfirmClick = () => {
        onChange(primary.selection.state, secondaryType ? secondary.selection.state : undefined);
        onClose();
    };

    const hasSelection = primary.selection.hasSelection || (secondaryType ? secondary.selection.hasSelection : false);

    return (
        <>
            <Modal.Header>{title}</Modal.Header>

            <Modal.Body>
                <div className="space-y-4">
                    <TemplateSection
                        label={secondaryType ? sectionLabels?.primary : undefined}
                        type={type}
                        {...primary}
                    />

                    {secondaryType ? (
                        <TemplateSection label={sectionLabels?.secondary} type={secondaryType} {...secondary} />
                    ) : null}
                </div>
            </Modal.Body>

            <Modal.Footer className="space-x-4">
                <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>

                <Button disabled={!hasSelection} onClick={handleConfirmClick}>
                    Confirm
                </Button>
            </Modal.Footer>
        </>
    );
};

const useTemplateSelection = (type: LineItemSelectorType | undefined, value?: SelectionState, allowedIds?: ID[]) => {
    let {
        data: lineItemTemplates,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
    } = useInfiniteCollection<LineItemTemplate>("lineItemTemplates", { type }, { enabled: Boolean(type) });

    if (type === LineItemType.DEMOGRAPHIC) {
        lineItemTemplates = lineItemTemplates.filter(
            (lineItemTemplate) => lineItemTemplate.code !== LineItemCode.PER_OUTING_PRICE,
        );
    }

    if (allowedIds) {
        lineItemTemplates = lineItemTemplates.filter((t) => allowedIds.includes(t.id));
    }

    const selection = useSelection(lineItemTemplates, hasNextPage ?? false, value);

    return { lineItemTemplates, hasNextPage, fetchNextPage, isFetchingNextPage, selection };
};

interface TemplateSectionProps extends ReturnType<typeof useTemplateSelection> {
    type: LineItemSelectorType;
    label?: string;
}

const TemplateSection = ({
    type,
    label,
    lineItemTemplates,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    selection,
}: TemplateSectionProps) => {
    return (
        <div className="overflow-hidden rounded-lg border border-gray-lighter">
            <div className="flex items-center justify-between bg-gray-lighter py-3 px-6">
                <Checkbox
                    checked={selection.isSelectedAll}
                    label={label ? `Select all ${label.toLowerCase()}` : "Select all items"}
                    data-testid="line-item-template-selector-modal-select-all-items-checkbox"
                    onChange={() => selection.selectAll()}
                />

                <span className="text-sm font-bold text-primary">
                    {selection.count === "All" ? (
                        <span>All items selected</span>
                    ) : (
                        <span>
                            <var data-var="selectionCount">{selection.count}</var>
                            <span> items selected</span>
                        </span>
                    )}
                </span>
            </div>

            <div className="p-3">
                <InfiniteList
                    hasMore={hasNextPage ?? false}
                    isLoading={isFetchingNextPage}
                    className="h-60 divide-y divide-gray-light overflow-y-auto overflow-x-hidden rounded border border-gray-light"
                    onLoadMore={fetchNextPage}
                >
                    {lineItemTemplates.map((lineItemTemplate) => (
                        <Checkbox
                            key={lineItemTemplate.id}
                            checked={selection.isSelected(lineItemTemplate)}
                            label={<LineItemTemplateCheckboxContent lineItemTemplate={lineItemTemplate} type={type} />}
                            className={clsx(
                                "px-3 py-2 font-bold",
                                selection.isSelected(lineItemTemplate) ? "bg-primary-lighter" : "odd:bg-gray-lighter",
                            )}
                            classNames={{ checkbox: "!border !border-gray" }}
                            onChange={() => {
                                selection.select(lineItemTemplate);
                            }}
                        />
                    ))}
                </InfiniteList>
            </div>
        </div>
    );
};

interface LineItemTemplateCheckboxContentProps {
    lineItemTemplate: LineItemTemplate;
    type: LineItemSelectorType;
}

const LineItemTemplateCheckboxContent = ({ lineItemTemplate, type }: LineItemTemplateCheckboxContentProps) => {
    return (
        <span className="flex items-center">
            <LineItemTemplateImage lineItemTemplate={lineItemTemplate} type={type} size="tiny" />
            <NoTranslate className="ml-2 font-bold text-black">{lineItemTemplate.name}</NoTranslate>
        </span>
    );
};
